import { Box, Typography } from "@mui/material";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  AthletesModel,
  CurrentAthleteWidgetData,
  Exercise,
  Exercises,
  getAttemptStatus,
  getAttemptValue,
  LiftField,
  LiftStatus,
  ThemeColors,
  SyncMeta,
} from "../../../util/src";
import dico from "../../../util/src/language/dico";
import WebsocketClient from "../../../util/src/websocket-client";
import RenderRecord from "../misc/RenderRecord";
import type { records } from "@prisma-local/client";
import CurrentAthleteWidgetChallengeHiver2025 from "./ChallengeHiver2025/CurrentAthleteWidget";

interface CurrentAthleteWidgetProps {
  meetId: string;
  icon?: string;
  token?: string;
  serverUrl?: string;
  records?: records[];
  colors: ThemeColors;
  theme?: string;
  durations?: { exit: number; showPR: number; PRDelay: number };
}

const CurrentAthleteWidget: React.FC<CurrentAthleteWidgetProps> = ({
  meetId,
  icon,
  serverUrl,
  records,
  token,
  colors,
  theme,
  durations,
}) => {
  const [currentAthlete, setCurrentAthlete] = useState<AthletesModel | null>(
    null,
  );

  const durationsCurrentAthlete = useMemo(
    () =>
      durations ?? {
        exit: 5,
        showPR: 10,
        PRDelay: 3,
      },
    [durations],
  );

  const [currentLift, setCurrentLift] = useState<LiftField | null>(null);
  const [currentMouv, setCurrentMouv] = useState<Exercise | null>(null);
  const [attemptNumber, setAttemptNumber] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [placementIfSuccessful, setPlacementIfSuccessful] = useState<
    number | null
  >(null);
  const [meetName, setMeetName] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [showPR, setShowPR] = useState(false);

  const getBackgroundColor = (status: LiftStatus) => {
    switch (status) {
      case LiftStatus.LIFT:
        return "rgba(76,176,81,0.98)"; // Vert pour réussite
      case LiftStatus.NO_LIFT:
        return "rgba(244,67,55,0.98)"; // Rouge pour échec
      default:
        return "transparent";
    }
  };
  const refreshRef = useRef<boolean>(true);
  const syncMetaRef = useRef<SyncMeta | null>(null);
  const [timer, setTimer] = useState("01:00");

  const formatTime = (totalSeconds: number) => {
    if (totalSeconds < 60)
      return `00:${totalSeconds < 10 ? "0" : ""}${String(totalSeconds)}`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const getPersonalBestKey = (
    exerciseName: string,
  ): "best_squat_kg" | "best_bench_kg" | "best_deadlift_kg" | null => {
    if (exerciseName === "squat") return "best_squat_kg";
    if (exerciseName === "bench") return "best_bench_kg";
    if (exerciseName === "deadlift") return "best_deadlift_kg";
    return null;
  };

  const getBestLabelKey = (
    exerciseName: string,
  ): "bestSquat" | "bestBench" | "bestDeadlift" | null => {
    if (exerciseName === "squat") return "bestSquat";
    if (exerciseName === "bench") return "bestBench";
    if (exerciseName === "deadlift") return "bestDeadlift";
    return null;
  };

  const currentAthleteIdRef = useRef<string | undefined>(undefined);
  const transitionTimeoutsRef = useRef<number[]>([]);
  const prTimeoutsRef = useRef<number[]>([]);

  const addTimeout = (
    ref: React.MutableRefObject<number[]>,
    fn: () => void,
    ms: number,
  ) => {
    const id = window.setTimeout(() => {
      ref.current = ref.current.filter((t) => t !== id);
      fn();
    }, ms);
    ref.current.push(id);
    return id;
  };

  const clearTimeouts = (ref: React.MutableRefObject<number[]>) => {
    for (const id of ref.current) window.clearTimeout(id);
    ref.current = [];
  };

  useEffect(() => {
    currentAthleteIdRef.current = currentAthlete?.id;
  }, [currentAthlete?.id]);

  useEffect(() => {
    syncMetaRef.current = null;
  }, [meetId, token, serverUrl]);

  useEffect(() => {
    const onCurrentAthlete = (payload: unknown) => {
      try {
        const data = payload as {
          payload: CurrentAthleteWidgetData | undefined;
          syncMeta: SyncMeta;
        };

        const last = syncMetaRef.current;
        const incoming = data.syncMeta;

        if (!last || incoming.sessionId !== last.sessionId) {
          syncMetaRef.current = incoming;
        } else if (incoming.rev <= last.rev) {
          return;
        } else {
          syncMetaRef.current = incoming;
        }

        if (!data.payload) {
          addTimeout(
            transitionTimeoutsRef,
            () => {
              setIsTransitioning(true);
              setShowPR(false);
              addTimeout(
                transitionTimeoutsRef,
                () => {
                  setCurrentAthlete(null);
                  setCurrentLift(null);
                  setPlacementIfSuccessful(null);
                  setMeetName("");
                  setStartDate(null);
                  setCurrentMouv(null);
                  setAttemptNumber(0);
                  setIsTransitioning(false);
                },
                500,
              );
            },
            durationsCurrentAthlete.exit * 1000,
          );
          return;
        }
        const {
          lift,
          athlete,
          exercise,
          attempt,
          placementIfSuccessful,
          meetName,
          startDate,
        } = data.payload;

        if (athlete?.id !== currentAthleteIdRef.current) {
          clearTimeouts(transitionTimeoutsRef);
          clearTimeouts(prTimeoutsRef);
          addTimeout(
            transitionTimeoutsRef,
            () => {
              refreshRef.current = false;
              setIsTransitioning(true);
              setShowPR(false);
              addTimeout(
                transitionTimeoutsRef,
                () => {
                  setCurrentAthlete(athlete || null);
                  setCurrentLift(lift);
                  setPlacementIfSuccessful(placementIfSuccessful);
                  setMeetName(meetName);
                  setStartDate(startDate ? new Date(startDate) : null);
                  if (lift) {
                    setCurrentMouv(exercise);
                    setAttemptNumber(attempt);
                  }
                  addTimeout(
                    transitionTimeoutsRef,
                    () => setIsTransitioning(false),
                    500,
                  );
                },
                500,
              );
            },
            refreshRef.current ? 0 : durationsCurrentAthlete.exit * 1000,
          );
        } else {
          setCurrentAthlete(athlete || null);
          setCurrentLift(lift);
          setPlacementIfSuccessful(placementIfSuccessful);
          setMeetName(meetName);
          setStartDate(startDate ? new Date(startDate) : null);
          if (lift) {
            const exercise =
              Exercises.find((e) => e.lifts.includes(lift)) || null;
            setCurrentMouv(exercise);
            const attempt = parseInt(lift.slice(-1));
            setAttemptNumber(attempt);
          }
        }
      } catch (error) {
        console.error(error);
      }
    };

    const onTimerDuration = (payload: unknown) => {
      try {
        const data = payload as string;
        setTimer(data);
      } catch (error) {
        console.error(error);
      }
    };

    const onTimerUpdate = (payload: unknown) => {
      try {
        const data = payload as { timeLeft: number };
        setTimer(formatTime(data.timeLeft));
      } catch (error) {
        console.error(error);
      }
    };

    const onceConnected = (wsClient: WebsocketClient) => {
      if (wsClient.isConnected()) {
        wsClient.off(`${meetId}/current-athlete`, onCurrentAthlete);
        wsClient.off("timerDuration", onTimerDuration);
        wsClient.off("timerUpdate", onTimerUpdate);
        wsClient.on(`${meetId}/current-athlete`, onCurrentAthlete);
        wsClient.on("timerDuration", onTimerDuration);
        wsClient.on("timerUpdate", onTimerUpdate);
        wsClient.emit("current-athlete", { id: meetId, auth_token: token });
        wsClient.emit("requestCurrentTimer");
      }
    };
    if (serverUrl) {
      const wsClient = WebsocketClient.getInstance(serverUrl);
      if (!wsClient.isConnected()) {
        wsClient.connect(() => onceConnected(wsClient));
      } else {
        onceConnected(wsClient);
      }
      return () => {
        wsClient.off(`${meetId}/current-athlete`, onCurrentAthlete);
        wsClient.off("timerDuration", onTimerDuration);
        wsClient.off("timerUpdate", onTimerUpdate);
        clearTimeouts(transitionTimeoutsRef);
        clearTimeouts(prTimeoutsRef);
      };
    }
  }, [meetId, token, serverUrl, durationsCurrentAthlete.exit]);

  // Gérer l'affichage du PR lors du changement d'athlète
  useEffect(() => {
    if (!currentAthlete?.id || !currentMouv) {
      setShowPR(false);
      return;
    }

    setShowPR(false);
    addTimeout(
      prTimeoutsRef,
      () => {
        setShowPR(true);
        addTimeout(
          prTimeoutsRef,
          () => {
            setShowPR(false);
          },
          durationsCurrentAthlete.showPR * 1000,
        );
      },
      durationsCurrentAthlete.PRDelay * 1000,
    );
    return () => {
      clearTimeouts(prTimeoutsRef);
      setShowPR(false);
    };
  }, [
    currentAthlete?.id,
    currentMouv,
    durationsCurrentAthlete.PRDelay,
    durationsCurrentAthlete.showPR,
  ]);

  useEffect(() => {
    if (error && !isTransitioning) {
      setError(null);
    }
  }, [error, isTransitioning]);

  const renderAttempts = () => {
    if (!currentAthlete || !currentLift) return null;

    const attempts = Array.from({ length: attemptNumber }, (_, i) => i + 1);

    return (
      <>
        {attempts.map((attempt) => {
          const status = getAttemptStatus(currentAthlete, currentLift, attempt);
          const value = getAttemptValue(currentAthlete, currentLift, attempt);
          const backgroundColor = getBackgroundColor(status);

          return (
            <Box
              key={attempt}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 150,
                  backgroundColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border:
                    status === LiftStatus.ANNONCED ? "1px solid white" : "none",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    color: "white",
                    textAlign: "center",
                    transform: "skewX(10deg)",
                  }}
                >
                  <RenderRecord
                    athlete={currentAthlete}
                    lift={currentLift}
                    total={false}
                    weight={Number(value)}
                    records={records ?? []}
                    sx={{
                      position: "absolute",
                      top: -10,
                      right: -40,
                      zIndex: 1,
                    }}
                    flagWidth={30}
                    flagHeight={20}
                    blink={status === LiftStatus.ANNONCED}
                  />
                  {value?.toString() || "-"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </>
    );
  };

  if ((!currentAthlete || error) && !isTransitioning) {
    return null;
  }

  const renderPowerScoutBanner = () => {
    const prKey = currentMouv ? getPersonalBestKey(currentMouv.name) : null;
    const prValue = currentAthlete?.powerscoutBests?.[
      prKey as keyof typeof currentAthlete.powerscoutBests
    ] as number | undefined;
    const totalValue = currentAthlete?.powerscoutBests?.best_total_kg;

    const shouldShow = currentAthlete && currentMouv && prKey && prValue;

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: totalValue ? "space-between" : "flex-start",
          gap: 1,
          minHeight: "45px", // Hauteur minimale pour maintenir l'espace
          visibility: shouldShow ? "visible" : "hidden",
        }}
      >
        {/* Bandeau PR du mouvement (sort de la gauche) */}
        <Box
          sx={{
            width: "40%",
            transition: "transform 0.5s ease-in-out",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2px 10px",
            backgroundColor: colors.primary,
            marginLeft: "15px",
            transform:
              showPR && shouldShow
                ? "translateX(0) skewX(-10deg)"
                : "translateX(-150%) skewX(-10deg)",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.primaryText,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transform: "skewX(10deg)",
              fontSize: "1.5em",
              fontWeight: "bold",
            }}
          >
            {prValue && currentMouv
              ? (() => {
                  const labelKey = getBestLabelKey(currentMouv.name);
                  return labelKey
                    ? `${dico().widget[labelKey]} : ${prValue} ${dico().widget.kg.toUpperCase()}`
                    : "";
                })()
              : ""}
          </Typography>
        </Box>

        {/* Bandeau PR Total pour deadlift (sort de la droite) */}
        {totalValue && (
          <Box
            sx={{
              width: "40%",
              transition: "transform 0.5s ease-in-out",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "2px 10px",
              backgroundColor: colors.primary,
              marginRight: "15px",
              transform:
                showPR && shouldShow && totalValue
                  ? "translateX(0) skewX(-10deg)"
                  : "translateX(150%) skewX(-10deg)",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: colors.primaryText,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                transform: "skewX(10deg)",
                fontSize: "1.5em",
                fontWeight: "bold",
              }}
            >
              {totalValue
                ? `${dico().widget.bestTotal} : ${totalValue} ${dico().widget.kg.toUpperCase()}`
                : ""}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderWeightClassAgeClassAndRank = () => {
    return (
      <Box
        sx={{
          width: "100%",
          transition: "transform 0.5s ease-in-out",
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          gap: 1,
          transform: isTransitioning
            ? "translateX(-100%) skewX(-10deg)"
            : "translateX(0) skewX(-10deg)",
        }}
      >
        <Box
          sx={{
            width: "15%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            padding: "10px",
            backgroundColor: colors.primary,
            marginLeft: "15px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.primaryText,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transform: "skewX(10deg)",
            }}
          >
            {currentAthlete?.catPoid?.nom} - {currentAthlete?.catAge?.code}
          </Typography>
        </Box>
        <Box
          sx={{
            width: "75%",
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            padding: "10px",
            paddingLeft: "25%",
            gap: 3,
            backgroundColor: colors.primary,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.primaryText,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transform: "skewX(10deg)",
            }}
          >
            {dico().rank.toUpperCase()}
          </Typography>
          {currentAthlete?.isDisqualified ? (
            <Typography
              variant="h5"
              sx={{
                color: "#FF0000",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                transform: "skewX(10deg)",
                fontSize: "1.8em",
                fontWeight: "bold",
              }}
            >
              {dico().guest}
            </Typography>
          ) : (
            <Typography
              variant="h5"
              sx={{
                color: colors.primaryText,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                transform: "skewX(10deg)",
                fontSize: "1.8em",
                fontWeight: "bold",
              }}
            >
              {dico().rankFromTo(
                currentAthlete?.placement ?? 0,
                placementIfSuccessful ?? 0,
              )}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: "10%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            padding: "10px",
            backgroundColor: colors.primary,
            marginRight: "15px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "rgb(255, 217, 0)",
              lineHeight: 1.2,
              fontSize: "1.8em",
              fontWeight: "bold",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transform: "skewX(10deg)",
            }}
          >
            {timer}
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderClubAndNames = () => {
    return (
      <Box
        sx={{
          width: "100%",
          transition: "transform 0.5s ease-in-out",
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          gap: 1,
          transform: isTransitioning
            ? "translateX(100%) skewX(-10deg)"
            : "translateX(0) skewX(-10deg)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
            overflow: "hidden",
            padding: "2px 10px",
            gap: 15,
            backgroundColor: colors.primary,
            marginLeft: "15px",
            marginRight: "15px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: colors.primaryText,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transform: "skewX(10deg)",
            }}
          >
            {currentAthlete?.club}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: colors.primaryText,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                transform: "skewX(10deg)",
                margin: 0,
              }}
            ></Typography>
            <Typography
              variant="h4"
              sx={{
                margin: 0,
                color: colors.primaryText,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                transform: "skewX(10deg)",
              }}
            >
              {currentAthlete?.prenom} {currentAthlete?.nom}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderMouvementAttempsAndTotal = () => {
    if (!currentAthlete || !currentLift) return null;
    return (
      <Box
        sx={{
          width: "100%",
          transition: "transform 0.5s ease-in-out",
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          gap: 1,
          transform: isTransitioning
            ? "translateX(100%) skewX(-10deg)"
            : "translateX(0) skewX(-10deg)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "left",
            overflow: "hidden",
            padding: "10px",
            gap: 10,
            backgroundColor: "rgba(0, 0, 0, 0.98)",
            marginLeft: "15px",
            marginRight: "15px",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "white",
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transform: "skewX(10deg)",
            }}
          >
            {dico()
              .mouvementName(
                currentMouv ?? { name: "squat", lifts: ["squat1"] },
              )
              .toUpperCase()}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "row", gap: 5 }}>
            {renderAttempts()}
          </Box>
          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
            <Box
              sx={{
                width: 150,
                backgroundColor: "rgba(187,160,11,0.98)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: "white",
                  fontWeight: "600",
                  textAlign: "center",
                  transform: "skewX(10deg)",
                }}
              >
                <RenderRecord
                  athlete={currentAthlete}
                  lift={currentLift}
                  total={true}
                  weight={Number(currentAthlete.totalkg)}
                  records={records ?? []}
                  position="left"
                  sx={{
                    position: "absolute",
                    top: -10,
                    zIndex: 1,
                  }}
                  flagWidth={30}
                  flagHeight={20}
                  blink
                />
                {currentAthlete?.totalkg}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderLogoMeetNameAndDate = () => {
    if (!icon) return;

    return (
      <Box
        sx={{
          width: "100%",
          transition: "transform 0.5s ease-in-out",
          overflow: "hidden",
          display: "flex",
          flexDirection: "row",
          gap: 1,
          transform: isTransitioning
            ? "translateX(-100%) skewX(-10deg)"
            : "translateX(0) skewX(-10deg)",
        }}
      >
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            overflow: "hidden",
            padding: "10px 10px",
            gap: 15,
            backgroundColor: colors.primary,
            marginLeft: "15px",
            marginRight: "15px",
          }}
        >
          <img
            src={icon}
            alt="9 sur 9 Solutions"
            style={{
              height: "auto",
              maxHeight: 30,
              width: "auto",
            }}
          />
          <Typography
            variant="h5"
            sx={{
              color: colors.primaryText,
              lineHeight: 1.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              transform: "skewX(10deg)",
              margin: 0,
            }}
          >
            {meetName.toUpperCase()} {new Date(startDate ?? "").getFullYear()}
          </Typography>
        </Box>
      </Box>
    );
  };

  if (
    theme === "Challenge d'Hiver 2025" &&
    currentAthlete &&
    currentLift &&
    currentMouv
  ) {
    return (
      <CurrentAthleteWidgetChallengeHiver2025
        records={records}
        showPR={showPR}
        attemptNumber={attemptNumber}
        currentAthlete={currentAthlete}
        currentLift={currentLift}
        currentMouv={currentMouv}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
      {renderPowerScoutBanner()}

      {renderWeightClassAgeClassAndRank()}

      {renderClubAndNames()}

      {renderMouvementAttempsAndTotal()}

      {renderLogoMeetNameAndDate()}
    </Box>
  );
};

import PropTypes from "prop-types";

CurrentAthleteWidget.propTypes = {
  meetId: PropTypes.string.isRequired,
  icon: PropTypes.string,
  serverUrl: PropTypes.string,
  records: PropTypes.array,
  token: PropTypes.string,
  colors: PropTypes.any.isRequired,
  theme: PropTypes.string,
  durations: PropTypes.any,
};

export default CurrentAthleteWidget;
